import React, { useState } from "react";
import { Shield, CheckCircle2, Euro, X, MapPin, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { NursingHomeFilters } from "@/hooks/use-county-page-data";

interface SmartFiltersProps {
  filters: NursingHomeFilters;
  setFilters: (filters: NursingHomeFilters) => void;
  activeFilterCount: number;
}

export function SmartFilters({ filters, setFilters, activeFilterCount }: SmartFiltersProps) {
  const [locating, setLocating] = useState(false);

  const clearAll = () => {
    setFilters({
      inspectionRating: null,
      fairDealOnly: false,
      priceRange: null,
      category: null,
      maxDistanceKm: null,
      userLocation: null,
    });
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFilters({
          ...filters,
          userLocation: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          maxDistanceKm: filters.maxDistanceKm || 25,
        });
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000 }
    );
  };

  const clearLocation = () => {
    setFilters({ ...filters, userLocation: null, maxDistanceKm: null });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mt-4">
      {/* Care Category */}
      <div className="flex items-center gap-1.5">
        <Building2 size={14} className="text-muted-foreground shrink-0" />
        <Select
          value={filters.category || "all"}
          onValueChange={(v) =>
            setFilters({ ...filters, category: v === "all" ? null : v })
          }
        >
          <SelectTrigger className="h-9 w-[190px] text-sm bg-background">
            <SelectValue placeholder="Care Category" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">Any Category</SelectItem>
            <SelectItem value="Private Nursing Home">Private</SelectItem>
            <SelectItem value="Public Nursing Home">Public (HSE)</SelectItem>
            <SelectItem value="Voluntary Nursing Home">Voluntary</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Inspection Rating */}
      <div className="flex items-center gap-1.5">
        <Shield size={14} className="text-muted-foreground shrink-0" />
        <Select
          value={filters.inspectionRating || "all"}
          onValueChange={(v) =>
            setFilters({ ...filters, inspectionRating: v === "all" ? null : v })
          }
        >
          <SelectTrigger className="h-9 w-[180px] text-sm bg-background">
            <SelectValue placeholder="HIQA Rating" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">Any Rating</SelectItem>
            <SelectItem value="compliant">Compliant</SelectItem>
            <SelectItem value="substantially_compliant">Substantially Compliant</SelectItem>
            <SelectItem value="non_compliant">Non-Compliant</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Fair Deal Toggle */}
      <div className="flex items-center gap-2 rounded-md border px-3 h-9 bg-background">
        <CheckCircle2 size={14} className="text-blue-600 shrink-0" />
        <Label htmlFor="fair-deal-filter" className="text-sm cursor-pointer whitespace-nowrap">
          Fair Deal
        </Label>
        <Switch
          id="fair-deal-filter"
          checked={filters.fairDealOnly}
          onCheckedChange={(checked) =>
            setFilters({ ...filters, fairDealOnly: checked })
          }
          className="scale-75"
        />
      </div>

      {/* Price Range */}
      <div className="flex items-center gap-1.5">
        <Euro size={14} className="text-muted-foreground shrink-0" />
        <Select
          value={filters.priceRange || "all"}
          onValueChange={(v) =>
            setFilters({ ...filters, priceRange: v === "all" ? null : v })
          }
        >
          <SelectTrigger className="h-9 w-[180px] text-sm bg-background">
            <SelectValue placeholder="Price Range" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="all">Any Price</SelectItem>
            <SelectItem value="under_1000">Under €1,000/week</SelectItem>
            <SelectItem value="1000_1300">€1,000 – €1,300/week</SelectItem>
            <SelectItem value="1300_1600">€1,300 – €1,600/week</SelectItem>
            <SelectItem value="over_1600">Over €1,600/week</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Distance Filter */}
      {filters.userLocation ? (
        <div className="flex items-center gap-1.5">
          <MapPin size={14} className="text-grandpal-green shrink-0" />
          <Select
            value={filters.maxDistanceKm?.toString() || "25"}
            onValueChange={(v) =>
              setFilters({ ...filters, maxDistanceKm: parseInt(v) })
            }
          >
            <SelectTrigger className="h-9 w-[150px] text-sm bg-background border-grandpal-green">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="10">Within 10 km</SelectItem>
              <SelectItem value="25">Within 25 km</SelectItem>
              <SelectItem value="50">Within 50 km</SelectItem>
              <SelectItem value="100">Within 100 km</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            onClick={clearLocation}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <X size={12} />
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleGetLocation}
          disabled={locating}
          className="h-9 text-sm gap-1.5 border-dashed"
        >
          {locating ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <MapPin size={14} />
          )}
          {locating ? "Locating..." : "Near me"}
        </Button>
      )}

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAll}
          className="h-9 text-sm text-muted-foreground hover:text-foreground gap-1"
        >
          <X size={14} />
          Clear {activeFilterCount > 1 ? `(${activeFilterCount})` : ""}
        </Button>
      )}
    </div>
  );
}
